/**
 * Thin client over the ratings API.
 * In development, the API runs at http://localhost:7071 via `func start`;
 * in production, the SPA calls the standalone Function App directly via CORS
 * (Function App URL is injected at build time as VITE_API_BASE).
 */
import { getConsent } from "./consent";
import { trackEvent } from "./analytics";

const envApiBase = (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE;
const apiBase =
  envApiBase && envApiBase.length > 0
    ? envApiBase.replace(/\/$/, "")
    : typeof window !== "undefined" && window.location.hostname === "localhost"
      ? "http://localhost:7071/api"
      : "/api";

export interface RatingSummary {
  count: number;
  avg: number;
}

export interface RatingsClient {
  getRating(recipeId: string): Promise<RatingSummary>;
  submitRating(recipeId: string, stars: number): Promise<RatingSummary>;
  getSummary(): Promise<Record<string, RatingSummary>>;
}

class HttpRatingsClient implements RatingsClient {
  async getRating(recipeId: string): Promise<RatingSummary> {
    const r = await fetch(`${apiBase}/ratings/${encodeURIComponent(recipeId)}`);
    if (!r.ok) throw new Error(`getRating ${recipeId}: ${r.status}`);
    return r.json();
  }
  async submitRating(recipeId: string, stars: number): Promise<RatingSummary> {
    const r = await fetch(`${apiBase}/ratings`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ recipeId, stars }),
    });
    if (r.status === 429) throw new ThrottleError();
    if (!r.ok) throw new Error(`submitRating ${recipeId}: ${r.status}`);
    if (getConsent() === "granted") trackEvent("rating_submit", { id: recipeId, stars });
    // Refetch the summary for that recipe.
    return this.getRating(recipeId);
  }
  async getSummary(): Promise<Record<string, RatingSummary>> {
    const r = await fetch(`${apiBase}/ratings`);
    if (!r.ok) return {};
    return r.json();
  }
}

export class ThrottleError extends Error {
  constructor() {
    super("You've already rated this recipe today.");
    this.name = "ThrottleError";
  }
}

let _client: RatingsClient | null = null;
export function getRatingsClient(): RatingsClient {
  if (!_client) _client = new HttpRatingsClient();
  return _client;
}

/** Test-only: override the client. */
export function _setRatingsClient(c: RatingsClient | null): void {
  _client = c;
}
